import { IGrid } from 'app/shared/model/grid.model';

export type LatLng = [number, number];
export type LatLngBounds = [LatLng, LatLng];

export const CELL_SIZE = 0.25;
export const GRID_DIM = 6;
export const GRID_EXTENT = CELL_SIZE * GRID_DIM;

/**
 * dim*dim cell bounds (SW/NE corners) extending NE (increasing lat/lng) from the origin.
 */
export function computeGridCells(originLat: number, originLng: number, cellSize: number = CELL_SIZE, dim: number = GRID_DIM): LatLngBounds[] {
  const cells: LatLngBounds[] = [];
  for (let row = 0; row < dim; row++) {
    for (let col = 0; col < dim; col++) {
      const sw: LatLng = [originLat + row * cellSize, originLng + col * cellSize];
      const ne: LatLng = [originLat + (row + 1) * cellSize, originLng + (col + 1) * cellSize];
      cells.push([sw, ne]);
    }
  }
  return cells;
}

/**
 * The 4 outer corner points of the whole grid (SW, SE, NE, NW), extending NE from the origin.
 */
export function computeGridCornerPoints(
  originLat: number,
  originLng: number,
  cellSize: number = CELL_SIZE,
  dim: number = GRID_DIM,
): LatLng[] {
  const extent = cellSize * dim;
  const sw: LatLng = [originLat, originLng];
  const se: LatLng = [originLat, originLng + extent];
  const ne: LatLng = [originLat + extent, originLng + extent];
  const nw: LatLng = [originLat + extent, originLng];
  return [sw, se, ne, nw];
}

/**
 * Grid corner points are stored as X/Y (longitude/latitude) pairs. Converts them to the
 * [lat, lng] tuples used by leaflet, skipping any point that isn't fully set.
 */
export function gridPointsToLatLngs(grid: IGrid): LatLng[] {
  const points: Array<[number | undefined, number | undefined]> = [
    [grid.point1Y, grid.point1X],
    [grid.point2Y, grid.point2X],
    [grid.point3Y, grid.point3X],
    [grid.point4Y, grid.point4X],
  ];
  return points.filter((point): point is LatLng => point[0] != null && point[1] != null);
}

export function latLngsToGridPoints(points: LatLng[]): Partial<IGrid> {
  const [p1, p2, p3, p4] = points;
  return {
    point1X: p1?.[1],
    point1Y: p1?.[0],
    point2X: p2?.[1],
    point2Y: p2?.[0],
    point3X: p3?.[1],
    point3Y: p3?.[0],
    point4X: p4?.[1],
    point4Y: p4?.[0],
  };
}

/**
 * The actual SW/NE bounds of the stored grid points. Grids only persist their 4 corner points
 * (not the cell size/dimension chosen when they were created), so this is the only geometry
 * that can be reliably reconstructed for an existing grid.
 */
export function gridToBounds(grid: IGrid): LatLngBounds | null {
  const points = gridPointsToLatLngs(grid);
  if (!points.length) {
    return null;
  }
  const lats = points.map(point => point[0]);
  const lngs = points.map(point => point[1]);
  const sw: LatLng = [Math.min(...lats), Math.min(...lngs)];
  const ne: LatLng = [Math.max(...lats), Math.max(...lngs)];
  return [sw, ne];
}

/**
 * The origin is the SW-most corner of the stored grid points (the lowest lat and lowest lng).
 */
export function gridToOrigin(grid: IGrid): LatLng | null {
  return gridToBounds(grid)?.[0] ?? null;
}
